import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO, injectJSONLD } from '@/hooks/useSEO';
import { aboutData, faqData } from '@/mocks/navigationData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useSEO({
    title: '关于蓝导航 - 精选网址导航平台',
    description: '了解蓝导航的品牌故事、服务理念与联系方式。蓝导航是专业的网址导航平台，汇聚海量优质网站资源，为用户提供便捷的上网入口。',
    keywords: '关于蓝导航,蓝导航品牌,蓝导航联系方式,网址导航平台介绍',
    canonicalUrl: 'https://www.lan-dao.com/about',
  });

  useEffect(() => {
    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于蓝导航',
      description: '了解蓝导航的品牌故事、服务理念与联系方式。',
      url: 'https://www.lan-dao.com/about',
      isPartOf: {
        '@type': 'WebSite',
        name: '蓝导航',
        url: 'https://www.lan-dao.com/',
      },
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.lan-dao.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '关于我们',
        },
      ],
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });

    injectJSONLD({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '蓝导航',
      url: 'https://www.lan-dao.com/',
      description: '蓝导航是一款专业的网址导航平台。',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '中关村科技园区',
        addressLocality: '北京市',
        addressRegion: '海淀区',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-0000',
        contactType: 'customer service',
        email: 'contact@lan-dao.com',
        hoursAvailable: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
        ],
      },
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9rgpf7nahb7382ovm9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;
        setFormStatus('error');
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后重试' : (typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <Breadcrumb />

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-10">
        <section className="mb-12 md:mb-16">
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于蓝导航
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              {aboutData.brand.slogan}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {aboutData.brand.introduction}
              </p>
              <div className="space-y-4 mt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className="ri-focus-2-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">我们的使命</h3>
                    <p className="text-sm text-foreground-600">{aboutData.brand.mission}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className="ri-eye-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">我们的愿景</h3>
                    <p className="text-sm text-foreground-600">{aboutData.brand.vision}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-[300px] md:h-[380px]">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20warm%20wood%20tones%20and%20large%20windows%2C%20team%20collaboration%20space%20with%20clean%20design%2C%20natural%20light%20flooding%20the%20room%2C%20professional%20yet%20inviting%20atmosphere%2C%20plants%20and%20warm%20accent%20colors%2C%20editorial%20interior%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="蓝导航办公环境"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {aboutData.brand.values.map((value, i) => (
              <div key={i} className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200 text-center">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                  <span className="font-heading text-lg font-bold">{i + 1}</span>
                </div>
                <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">{value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12 md:mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aboutData.reputation.map((review, i) => (
              <div key={i} className="bg-background-50 rounded-xl p-5 border border-background-200">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <i
                      key={j}
                      className={`text-sm ${j < review.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{review.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="font-heading text-sm font-bold text-accent-600">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12 md:mb-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center">
            常见问题
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqData.map((faq, i) => (
              <details key={i} className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-100 transition-colors">
                    <i className="ri-add-line text-sm text-foreground-500 group-open:hidden"></i>
                    <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                  </div>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2 text-center">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            有任何问题或建议？欢迎随时与我们取得联系
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-background-50 rounded-xl p-5 border border-background-200 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">电子邮箱</h3>
              <p className="text-xs text-foreground-500">{aboutData.contact.email}</p>
            </div>
            <div className="bg-background-50 rounded-xl p-5 border border-background-200 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-3">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">联系电话</h3>
              <p className="text-xs text-foreground-500">{aboutData.contact.phone}</p>
            </div>
            <div className="bg-background-50 rounded-xl p-5 border border-background-200 text-center">
              <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-3">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1">办公地址</h3>
              <p className="text-xs text-foreground-500">{aboutData.contact.address}</p>
            </div>
          </div>

          <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">
              发送消息
            </h3>

            {formStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">消息已发送！</h4>
                <p className="text-sm text-foreground-500">感谢您的来信，我们将在1-2个工作日内回复您。</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-4 px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  发送新消息
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    消息内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                    placeholder="请输入您的消息内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                {formStatus === 'error' && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                    {formError || '提交失败，请稍后重试'}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full sm:w-auto px-8 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  发送消息
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}